<script lang="ts">
	import Image from '$lib/components/image.svelte';
	import { buttonVariants } from '$lib/components/ui/button';
	import type { UploadFileFieldsFragment } from '$lib/graphql/strapiApi';
	import { cn } from '$lib/utils.js';
	import { mode } from '$lib/state/global.svelte';
	import { Link } from 'lucide-svelte';
	import { onDestroy, onMount } from 'svelte';

	type Feature = {
		title?: string | null;
		description?: string | null;
		richDescription?: { body?: string | null } | null;
		media?: UploadFileFieldsFragment | null;
		darkMedia?: UploadFileFieldsFragment | null;
		url?: string | null;
		text?: string | null; // used as image css for now
	};
	let {
		features,
		heading,
		description
	}: { heading?: string | null; description?: string | null; features: Feature[] } = $props();

	let selectedFeatureState = $state(features[0]);

	let selectedFeature = $derived(
		features.length === 1 && !features[0].title ? features[0] : selectedFeatureState
	);

	let timeoutId: ReturnType<typeof setTimeout>;

	function loop(start?: boolean) {
		if (!start) {
			const idx = features.findIndex((f) => f.title === selectedFeatureState?.title);
			let nextIdx = idx + 1;
			if (nextIdx === features.length) {
				nextIdx = 0;
			}
			selectedFeatureState = features[nextIdx];
		}
		timeoutId = setTimeout(loop, 30000);
	}

	onMount(() => {
		if (features.length > 1) {
			loop(true);
		}
	});

	onDestroy(() => {
		if (features.length > 1) {
			clearTimeout(timeoutId);
		}
	});

	const selectFeature = (feature: Feature) => {
		selectedFeatureState = feature;
		if (features.length > 1) {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(loop, 10000);
		}
	};

	let getImageSrc = (feature: Feature) => {
		let imageSrc;
		if ($mode === 'dark') {
			imageSrc =
				feature.darkMedia?.formats?.xlarge?.url ??
				feature.darkMedia?.url ??
				feature.media?.formats?.xlarge?.url ??
				feature.media?.url;
		} else {
			imageSrc = feature.media?.formats?.xlarge?.url ?? feature.media?.url;
		}
		return imageSrc;
	};
</script>

<div class="flex flex-col gap-y-6">
	{#if heading}
		<h3 class="flex justify-center text-center text-3xl font-semibold">{heading}</h3>
	{/if}
	{#if description}
		<p
			class="mb-2 inline justify-center text-center text-xl [&_a:hover]:underline [&_a]:inline [&_a]:font-semibold"
		>
			{@html description}
		</p>
	{/if}
	{#if features.length > 1 || features[0]?.title}
		<div class="flex flex-row flex-wrap justify-center gap-2">
			{#each features as feature}
				<button
					class={cn(
						buttonVariants({ size: 'sm', variant: 'ghost' }),
						'px-6 py-4 text-lg',
						`${selectedFeature.title === feature.title ? 'bg-muted' : ''}`
					)}
					onclick={() => selectFeature(feature)}
				>
					<h4 class="">{feature.title}</h4>
				</button>
			{/each}
		</div>
	{/if}
	{#if selectedFeature?.richDescription?.body || selectedFeature?.description}
		{#if selectedFeature.url}
			<a
				href={selectedFeature.url}
				class={cn(
					buttonVariants({ size: 'sm', variant: 'ghost' }),
					'm-auto flex h-auto w-fit justify-center text-wrap px-4 py-1 text-center text-lg font-medium'
				)}
			>
				<span class="inline"
					>{@html selectedFeature.richDescription?.body || selectedFeature.description}
					<Link class="inline" /></span
				></a
			>
		{:else}
			<p class="flex justify-center text-wrap px-4 text-justify text-lg font-medium">
				{@html selectedFeature.richDescription?.body || selectedFeature.description}
			</p>
		{/if}
	{/if}
	{#if selectedFeature?.media?.url}
		<Image
			wrapperClass={selectedFeature.text}
			class="rounded-xl drop-shadow-[0_0_8px_rgba(0,0,0,0.25)]"
			src={getImageSrc(selectedFeature)}
			alt={selectedFeature.title}
		/>
	{/if}
</div>
